import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import { ComplianceClient } from 'src/app/compliance/api/compliance-client.service';
import { BackgroundCheckClient } from 'src/app/compliance/api/background-check-client.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { Owner } from 'src/app/shared/helpers/owner.decorator';
import { ComplianceReportClient } from '../../api/compliance-report-client.service';
import { ContractsClient } from '../../api/contracts-client.service';
import { JobRequirementsClient } from '../../api/job-requirements-client.service';
import { ComplianceReportRequirementDto } from '../../api/models/compliance-report-requirement-dto.model';
import { ComplianceReportRequirementNoteDto } from '../../api/models/compliance-report-requirement-note-dto.model';
import { ComplianceStatusDto } from '../../api/models/compliance-status-dto.model';
import { ComplianceReportHelperService } from '../../compliance-report-new/shared/services/compliance-report-helper.service';
import {
    automatchSignalReceived,
    getAutomatchData,
    getAutomatchDataFailure,
    getAutomatchDataSuccess,
    getAvailableStatuses,
    getAvailableStatusesFailure,
    getAvailableStatusesSuccess,
    getCurrentContract,
    getCurrentContractFailure,
    getCurrentContractSuccess,
    getCurrentPermissions,
    getCurrentPermissionsFailure,
    getCurrentPermissionsSuccess,
    getJobRequirementsChanges,
    getJobRequirementsChangesFailure,
    getJobRequirementsChangesSuccess,
    getReadinessStatuses,
    getReadinessStatusesFailure,
    getReadinessStatusesSuccess,
    markAsReviewed,
    markAsReviewedFailure,
    markAsReviewedSuccess,
    processSpecialRules,
    processSpecialRulesFailure,
    processSpecialRulesSuccess,
    getCandidateReviewAuditLogs,
    getCandidateReviewAuditLogsSuccess,
    getCandidateReviewAuditLogsFailure,
    qualifyContract,
    qualifyRequirements,
    putRequirements,
    saveRequirements,
    saveRequirementsFailure,
    saveRequirementsSuccess,
    saveSelectedCandidateDocument,
    saveSelectedCandidateDocumentFailure,
    saveSelectedCandidateDocumentSuccess,
    setSingleSignalAutomatchSuccess,
    updateAyaClearedStatusForCurrentContract,
    updateAyaClearedStatusForCurrentContractFailure,
    updateAyaClearedStatusForCurrentContractSuccess,
    updateClientClearedStatusForCurrentContract,
    updateClientClearedStatusForCurrentContractFailure,
    updateClientClearedStatusForCurrentContractSuccess,
    updateComplianceNoteForRequirement,
    updateComplianceNoteForRequirementFailure,
    updateComplianceNoteForRequirementSuccess,
    updateComplianceReportNote,
    updateComplianceReportNoteFailure,
    updateComplianceReportNoteSuccess,
    updateContractComplianceStatus,
    updateCurrentComplianceStatus,
    updateRequirementsForCurrentContract,
    updateRequirementsForCurrentContractFailure,
    updateRequirementsForCurrentContractSuccess,
    qualifyRequirementsSuccess,
    qualifyRequirementsFailure,
    getIntegrationStatuses,
    getIntegrationStatusesSuccess,
    getIntegrationStatusesFailure,
    getHealthIntegrationStatuses,
    getHealthIntegrationStatusesFailure,
    getHealthIntegrationStatusesSuccess
} from '../actions/compliance-report-new.actions';
import { HubConnectionService } from 'src/app/shared/services/realtime-connection.service';
import { ComplianceRequestsClient } from 'src/app/compliance/api/compliance-requests-client.service';

@Owner('Mic Drop')
@Injectable()
export class ComplianceReportNewEffects {
    constructor(
        private readonly _actions$: Actions,
        private readonly complianceReportClient: ComplianceReportClient,
        private readonly _contractsClient: ContractsClient,
        private readonly _complianceClient: ComplianceClient,
        private readonly _complianceReportHelperService: ComplianceReportHelperService,
        private readonly jobRequirementsClient: JobRequirementsClient,
        private readonly _complianceReportHubService: HubConnectionService,
        private readonly _backgroundCheckClient: BackgroundCheckClient,
        private readonly _complianceRequestsService: ComplianceRequestsClient
    ) {}

    getCurrentContract$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getCurrentContract),
            switchMap((action) =>
                this.complianceReportClient.getComplianceReport(action.contractId).pipe(
                    switchMap((currentContract) =>
                        of(
                            getCurrentContractSuccess({ currentContract }),
                            updateCurrentComplianceStatus({ complianceStatus: currentContract.complianceStatus })
                        )
                    ),
                    catchError((result) => {
                        return of(getCurrentContractFailure({ contractId: action.contractId, error: result.message }));
                    })
                )
            )
        )
    );

    processSpecialRules$ = createEffect(() =>
        this._actions$.pipe(
            ofType(processSpecialRules),
            switchMap((action) =>
                this.complianceReportClient.processSpecialRules(action.specialRulesAutoMatchRequestDto).pipe(
                    map((SpecialRulesRequirementDto) => processSpecialRulesSuccess({ SpecialRulesRequirementDto })),
                    catchError((result) => {
                        return of(processSpecialRulesFailure({ error: result.message }));
                    })
                )
            )
        )
    );

    getCurrentPermissions$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getCurrentPermissions),
            switchMap(() =>
                this.complianceReportClient.permissions().pipe(
                    map((permissions) => getCurrentPermissionsSuccess({ permissions })),
                    catchError((result) => {
                        return of(getCurrentPermissionsFailure({ error: result.message }));
                    })
                )
            )
        )
    );

    updateRequirementsForCurrentContract$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateRequirementsForCurrentContract),
            switchMap((action) =>
                this.complianceReportClient.getRequirements(action.contractId).pipe(
                    map((requirements) => updateRequirementsForCurrentContractSuccess({ requirements })),
                    catchError((result) => {
                        console.error(result);
                        return of(updateRequirementsForCurrentContractFailure({ error: result.message }));
                    })
                )
            )
        )
    );

    updateAyaClearedStatusForCurrentContract$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateAyaClearedStatusForCurrentContract),
            switchMap((action) =>
                this.complianceReportClient.updateComplianceReportStatus(action.contractId, action.status).pipe(
                    map((_) =>
                        updateAyaClearedStatusForCurrentContractSuccess({
                            contractId: action.contractId,
                            status: action.status
                        })
                    ),
                    catchError((result) => {
                        return of(
                            updateAyaClearedStatusForCurrentContractFailure({
                                contractId: action.contractId,
                                status: action.status
                            })
                        );
                    })
                )
            )
        )
    );

    updateClientClearedStatusForCurrentContract$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateClientClearedStatusForCurrentContract),
            switchMap((action) =>
                this.complianceReportClient.updateComplianceReportStatus(action.contractId, action.status).pipe(
                    map((_) =>
                        updateClientClearedStatusForCurrentContractSuccess({
                            contractId: action.contractId,
                            status: action.status
                        })
                    ),
                    catchError((result) => {
                        return of(
                            updateClientClearedStatusForCurrentContractFailure({
                                contractId: action.contractId,
                                status: action.status
                            })
                        );
                    })
                )
            )
        )
    );

    getAvailableStatuses$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getAvailableStatuses),
            switchMap(() =>
                this._complianceClient.getAllStatuses().pipe(
                    map((availableStatuses) => getAvailableStatusesSuccess({ availableStatuses })),
                    catchError((result) => {
                        console.error(result);
                        return of(getAvailableStatusesFailure({ error: result.message }));
                    })
                )
            )
        )
    );

    getReadinessStatuses$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getReadinessStatuses),
            switchMap(() =>
                this._complianceClient.getReadinessStatuses().pipe(
                    map((readinessStatuses) => getReadinessStatusesSuccess({ readinessStatuses })),
                    catchError((result) => {
                        console.error(result);
                        return of(getReadinessStatusesFailure({ error: result.message }));
                    })
                )
            )
        )
    );

    qualifyContract$ = createEffect(() =>
        this._actions$.pipe(
            ofType(qualifyContract),
            map((action) =>
                action.useSelectionMapAsDefaults
                    ? this._complianceReportHelperService.getQualifiedRequirementsRequest(action)
                    : this._complianceReportHelperService.getQualifyRequest(action)
            ),
            switchMap((result) => this.qualify(result))
        )
    );

    qualifyRequirements$ = createEffect(() =>
        this._actions$.pipe(
            ofType(qualifyRequirements),
            map((action) =>
                this._complianceReportHelperService.updateRequirementsWithModifiedRequirements(
                    action.contractId,
                    action.requirements,
                    action.modifiedRequirements,
                    action.automatchData
                )
            ),
            map((action) =>
                this._complianceReportHelperService.getComplianceReportRequirementDtoFromQualifyRequirementsActionParameters(
                    action.contractId,
                    action.requirements,
                    action.oldRequirements,
                    action.modifiedRequirements,
                    action.automatchData
                )
            ),
            switchMap((result) => this.qualify(result))
        )
    );

    private qualify = (data: { contractId: number; requirements: ComplianceReportRequirementDto[] }) =>
        this.complianceReportClient.saveComplianceReportRequirements(data.contractId, true, data.requirements).pipe(
            switchMap(() =>
                of(
                    saveRequirementsSuccess(),
                    qualifyRequirementsSuccess(),
                    getCurrentContract({ contractId: data.contractId })
                )
            ),
            catchError((error) => {
                console.error(error);
                return of(
                    qualifyRequirementsFailure({ error: error.message }),
                    saveRequirementsFailure({ error: error.message })
                );
            })
        );

    putRequirements$ = createEffect(() =>
        this._actions$.pipe(
            ofType(putRequirements),
            map((action) => {
                return {
                    contractId: action.contractId,
                    requirements: action.modifiedRequirements?.map((modifiedRequirement) => {
                        const originalRequirement = action.originalRequirements?.find(
                            (oldReq) =>
                                oldReq.documentTypeId === modifiedRequirement.documentTypeId &&
                                oldReq.requirementId === modifiedRequirement.requirementId
                        );

                        const selectedCandidateDocId = modifiedRequirement.documents.find((a) => a.isSelected === true);
                        const originalSelectedCandidateDocId = originalRequirement?.documents.find(
                            (a) => a.isSelected === true
                        );

                        return {
                            otherRequirementId:
                                selectedCandidateDocId?.requirementId ?? originalSelectedCandidateDocId?.requirementId,
                            documentTypeId: modifiedRequirement.documentTypeId,
                            candidateDocumentId:
                                selectedCandidateDocId?.candidateDocumentId ??
                                originalSelectedCandidateDocId?.candidateDocumentId,
                            selected: !!selectedCandidateDocId,
                            required: modifiedRequirement.isRequired,
                            overrideLevel: modifiedRequirement.overrideLevel,
                            requiredOverridden: modifiedRequirement.isRequired !== originalRequirement.isRequired,
                            requiresManualReview: modifiedRequirement.requiresManualReview,
                            isRequiredByClient: null
                        } as ComplianceReportRequirementDto;
                    })
                };
            }),
            switchMap((result) =>
                this.complianceReportClient
                    .saveComplianceReportRequirements(result.contractId, true, result.requirements)
                    .pipe(
                        switchMap(() =>
                            of(saveRequirementsSuccess(), getCurrentContract({ contractId: result.contractId }))
                        ),
                        catchError((error) => {
                            console.error(error);
                            return of(saveRequirementsFailure({ error: error.message }));
                        })
                    )
            ),
            catchError((error) => {
                console.error(error);
                return of(saveRequirementsFailure({ error: error.message }));
            })
        )
    );

    saveRequirements$ = createEffect(() =>
        this._actions$.pipe(
            ofType(saveRequirements),
            map((action) => {
                return {
                    contractId: action.contractId,
                    requirements: action.requirements?.map((req) => {
                        const selectedCandidateDocId =
                            this._complianceReportHelperService.getSelectedCandidateDocumentId(
                                action.oldRequirements,
                                req
                            );
                        const selectedRequirementId =
                            this._complianceReportHelperService.getRequirementIdFromSelectedDocument(
                                action.oldRequirements,
                                req
                            );
                        const newIsRequiredValue = this._complianceReportHelperService.getIsRequiredValue(
                            action.oldRequirements,
                            req
                        );
                        const oldIsRequiredValue = action.oldRequirements?.find(
                            (oldReq) => oldReq.documentTypeId === req.documentTypeId
                        ).isRequired;

                        const newIsSelectedValue = this._complianceReportHelperService.isNewDocumentBeingSelected(
                            action.oldRequirements,
                            req
                        );
                        const returnValue = {
                            otherRequirementId: selectedRequirementId,
                            documentTypeId: req.documentTypeId,
                            candidateDocumentId: selectedCandidateDocId,
                            selected: newIsSelectedValue,
                            required: newIsRequiredValue,
                            requiresManualReview: req.requiresManualReview,
                            isRequiredByClient: null,
                            overrideLevel: req.overrideLevel,
                            requiredOverridden: req.isRequired !== oldIsRequiredValue
                        } as ComplianceReportRequirementDto;

                        return returnValue;
                    })
                };
            }),
            switchMap((result) =>
                this.complianceReportClient
                    .saveComplianceReportRequirements(result.contractId, true, result.requirements)
                    .pipe(
                        switchMap(() =>
                            of(saveRequirementsSuccess(), getCurrentContract({ contractId: result.contractId }))
                        ),
                        catchError((error) => {
                            console.error(error);
                            return of(saveRequirementsFailure({ error: error.message }));
                        })
                    )
            ),
            catchError((error) => {
                console.error(error);
                return of(saveRequirementsFailure({ error: error.message }));
            })
        )
    );

    saveSelectedCandidateDocument$ = createEffect(() =>
        this._actions$.pipe(
            ofType(saveSelectedCandidateDocument),
            map((action) => {
                return {
                    isQualified: action.isQualified,
                    contractId: action.contractId,
                    requirements: [
                        {
                            otherRequirementId: action.candidateDocument.requirementId,
                            documentTypeId: action.requirement.documentTypeId,
                            candidateDocumentId: action.candidateDocument.candidateDocumentId,
                            selected: action.isSelected,
                            required: action.requirement.isRequired,
                            requiresManualReview: action.requirement.requiresManualReview,
                            isRequiredByClient: null,
                            overrideLevel: action.requirement.overrideLevel
                        } as ComplianceReportRequirementDto
                    ] as ComplianceReportRequirementDto[]
                };
            }),
            switchMap((result) => {
                if (result.isQualified) {
                    return this.complianceReportClient
                        .saveComplianceReportRequirements(result.contractId, false, result.requirements)
                        .pipe(
                            switchMap((result: ComplianceStatusDto) =>
                                of(
                                    saveSelectedCandidateDocumentSuccess(),
                                    updateContractComplianceStatus({ complianceStatus: result.complianceStatusId })
                                )
                            ),
                            catchError((result) => {
                                console.error(result);
                                return of(saveSelectedCandidateDocumentFailure({ error: result.message }));
                            })
                        );
                }
                return of(saveSelectedCandidateDocumentSuccess());
            })
        )
    );

    updateComplianceReportNote$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceReportNote),
            switchMap((action) =>
                this._contractsClient
                    .updateContract({
                        contractIds: [action.contractId],
                        complianceState: {
                            note: action.complianceReportNote as string,
                            isUpdatedNote:
                                (action.complianceReportNote as string) !== null &&
                                (action.complianceReportNote as string) !== undefined
                                    ? true
                                    : false
                        }
                    } as any)
                    .pipe(
                        map(() =>
                            updateComplianceReportNoteSuccess({
                                contractId: action.contractId,
                                note: action.complianceReportNote
                            })
                        ),
                        catchError((result) => {
                            console.error(result);
                            return of(
                                updateComplianceReportNoteFailure({ contractId: action.contractId, error: result })
                            );
                        })
                    )
            )
        )
    );

    updateComplianceNoteForRequirement$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceNoteForRequirement),
            switchMap((action) =>
                this.complianceReportClient
                    .addRequirementComplianceNote(action.contractId, action.requirementId, {
                        note: action.complianceNote || '',
                        documentTypeId: action.documentTypeId,
                        isRequired: action.isRequired,
                        appendToExistingNote: null
                    })
                    .pipe(
                        map((note: ComplianceReportRequirementNoteDto) =>
                            updateComplianceNoteForRequirementSuccess({
                                requirementId: action.requirementId,
                                documentTypeId: action.documentTypeId,
                                savedComplianceNote: note,
                                addOrEdit: action.addOrEdit
                            })
                        ),
                        catchError((result) => {
                            console.error(result);
                            return of(saveRequirementsFailure({ error: result.message }));
                        })
                    )
            )
        )
    );

    updateAyaClearedStatusForCurrentContractSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateAyaClearedStatusForCurrentContractSuccess),
            map(() => showToasterMessage({ message: 'Successfully updated clearance status' }))
        )
    );

    updateAyaClearedStatusForCurrentContractFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateAyaClearedStatusForCurrentContractFailure),
            map(() => showToasterMessage({ message: 'Failed to Update clearance status' }))
        )
    );

    updateClientClearedStatusForCurrentContractSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateClientClearedStatusForCurrentContractSuccess),
            map(() => showToasterMessage({ message: 'Successfully updated clearance status' }))
        )
    );

    updateClientClearedStatusForCurrentContractFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateClientClearedStatusForCurrentContractFailure),
            map(() => showToasterMessage({ message: 'Failed to Update clearance status' }))
        )
    );

    updateComplianceNoteForRequirementSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceNoteForRequirementSuccess),
            map((action) =>
                showToasterMessage({ message: `Successfully ${(action.addOrEdit || 'Add').toLowerCase()}ed note` })
            )
        )
    );

    saveRequirementsSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(saveRequirementsSuccess),
            map(() => showToasterMessage({ message: 'Successfully Updated Requirements' }))
        )
    );

    updateComplianceReportNoteSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceReportNoteSuccess),
            switchMap((action) =>
                of(
                    getCurrentContract({ contractId: action.contractId }),
                    showToasterMessage({ message: 'Successfully Updated Compliance Report Note' })
                )
            )
        )
    );

    updateComplianceNoteForRequirementFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceNoteForRequirementFailure),
            map((action) => getCurrentContract({ contractId: action.contractId })),
            map(() => showToasterMessage({ message: 'Failed to Update Compliance Note' }))
        )
    );

    saveRequirementsFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(saveRequirementsFailure),
            tap((action) => console.error('Save Requirements Failed: ', action.error)),
            map(() => showToasterMessage({ message: 'Save Requirements Failed' }))
        )
    );

    updateComplianceReportNoteFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateComplianceReportNoteFailure),
            switchMap((action) =>
                of(
                    getCurrentContract({ contractId: action.contractId }),
                    showToasterMessage({ message: 'Failed to Update Compliance Report Note' })
                )
            )
        )
    );

    getAutomatchData$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getAutomatchData),
            switchMap((action) =>
                this.complianceReportClient.autoMatchRequirements(action.automatchRequestData).pipe(
                    map((automatchData) => getAutomatchDataSuccess({ automatchData })),
                    catchError((result) => {
                        console.error(result);
                        return of(getAutomatchDataFailure({ error: result }));
                    })
                )
            )
        )
    );

    getJobRequirementsChanges$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getJobRequirementsChanges),
            switchMap((action) =>
                this.jobRequirementsClient.getJobRequirementChanges(action.jobId, action.contractId).pipe(
                    map((jobRequirements) => getJobRequirementsChangesSuccess({ jobRequirements })),
                    catchError((result) => {
                        console.error(result);
                        return of(getJobRequirementsChangesFailure({ error: result }));
                    })
                )
            )
        )
    );

    automatchSignalReceived$ = createEffect(() =>
        this._actions$.pipe(
            ofType(automatchSignalReceived),
            map((action) => setSingleSignalAutomatchSuccess({ automatchData: action.automatchData }))
        )
    );

    markAsReviewed$ = createEffect(() =>
        this._actions$.pipe(
            ofType(markAsReviewed),
            switchMap((action) =>
                this.complianceReportClient.markAsReviewed(action.contractId).pipe(
                    map((status) => markAsReviewedSuccess({ contractId: action.contractId, complianceStatus: status })),
                    catchError((result) => {
                        console.error(result);
                        return of(markAsReviewedFailure({ error: result }));
                    })
                )
            )
        )
    );

    markAsReviewedSuccess$ = createEffect(() =>
        this._actions$.pipe(
            ofType(markAsReviewedSuccess),
            switchMap((action) =>
                of(
                    getCurrentContract({ contractId: action.contractId }),
                    getAvailableStatuses(),
                    showToasterMessage({ message: 'Successfully updated Compliance Status and requirements.' })
                )
            )
        )
    );

    markAsReviewedFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(markAsReviewedFailure),
            tap((action) => console.error('Mark As Reviewed Failed: ', action.error)),
            map(() => showToasterMessage({ message: 'Mark As Reviewed Failed' }))
        )
    );

    getIntegrationStatuses$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getIntegrationStatuses),
            switchMap((action) => {
                if (action.contractId > 0) {
                    return this._backgroundCheckClient.getIntegrationStatusesByContractId(action.contractId).pipe(
                        map((response) => {
                            const integrationStatuses: { [contractId: number]: string } = {};

                            if (response.length > 0) {
                                const statusLines: string[] = [];
                                response.forEach((status) => {
                                    const updatedDateStr = new Date(status.updatedDateUtc).toLocaleDateString('en-US');
                                    if (status.orderStatus === 'Snoozed') {
                                        const snoozedDate = status.snoozedUntilDate;
                                        if (snoozedDate) {
                                            const snoozedDateStr = new Date(snoozedDate).toLocaleDateString('en-US');

                                            statusLines.push(
                                                `Order ${status.orderNumber}: status Snoozed, Snoozed until: ${snoozedDateStr}|${status.updatedBy}| ${updatedDateStr}`
                                            );
                                        } else {
                                            statusLines.push(
                                                `Order ${status.orderNumber}: status Snoozed|${status.updatedBy}| ${updatedDateStr}`
                                            );
                                        }
                                    } else {
                                        const eta = status.eta;
                                        if (eta) {
                                            const etaDate = new Date(eta).toLocaleDateString('en-US');
                                            statusLines.push(
                                                `Order ${status.orderNumber}: status ${status.orderStatus}, ETA: ${etaDate}|${status.updatedBy}| ${updatedDateStr}`
                                            );
                                        } else {
                                            statusLines.push(
                                                `Order ${status.orderNumber}: status ${status.orderStatus}|${status.updatedBy}| ${updatedDateStr}`
                                            );
                                        }
                                    }
                                });

                                const combinedStatus = statusLines.join('\n');
                                integrationStatuses[action.contractId] = combinedStatus;
                            }
                            return getIntegrationStatusesSuccess({ integrationStatuses });
                        }),
                        catchError((error) => {
                            return of(
                                getIntegrationStatusesFailure({
                                    error: error?.message || 'Failed to fetch integration statuses'
                                })
                            );
                        })
                    );
                } else {
                    return of(
                        getIntegrationStatusesFailure({
                            error: 'Contract ID is required and must be a valid, non-zero number'
                        })
                    );
                }
            })
        )
    );

    getIntegrationStatusesFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getIntegrationStatusesFailure),
            map(() => showToasterMessage({ message: 'Failed to load integration statuses' }))
        )
    );

    getCandidateReviewAuditLogs$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getCandidateReviewAuditLogs),
            switchMap((action) => {
                if (action.contractId <= 0) {
                    return of(
                        getCandidateReviewAuditLogsFailure({
                            error: 'Contract ID is required and must be a valid, non-zero number'
                        })
                    );
                } else {
                    // Pass eventTypeId = 3 for CandidateAcknowledge events only
                    const eventTypeId = action.eventTypeId ?? 3;
                    return this.complianceReportClient.getCandidateReviewAuditLogs(action.contractId, eventTypeId).pipe(
                        map((response) => {
                            return getCandidateReviewAuditLogsSuccess({ auditLogs: response });
                        }),
                        catchError((error: unknown) => {
                            const errorMessage =
                                error instanceof Error ? error.message : 'Failed to fetch candidate review audit logs';
                            return of(
                                getCandidateReviewAuditLogsFailure({
                                    error: errorMessage
                                })
                            );
                        })
                    );
                }
            })
        );
    });

    getCandidateReviewAuditLogsFailure$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getCandidateReviewAuditLogsFailure),
            map(() => showToasterMessage({ message: 'Failed to load candidate review audit logs' }))
        );
    });

    getHealthIntegrationStatuses$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getHealthIntegrationStatuses),
            switchMap((action) => {
                if (action.contractId != null) {
                    return this._complianceRequestsService.getIntegrationStatusesByContractId(action.contractId).pipe(
                        map((response) => {
                            return getHealthIntegrationStatusesSuccess({
                                healthIntegrationStatuses: response
                            });
                        }),
                        catchError((error) => {
                            return of(
                                getHealthIntegrationStatusesFailure({
                                    error: error?.message || 'Failed to fetch health integration statuses'
                                })
                            );
                        })
                    );
                } else {
                    return of(
                        getHealthIntegrationStatusesFailure({
                            error: 'Contract ID is required and must be a valid, non-zero number'
                        })
                    );
                }
            })
        )
    );

    getHealthIntegrationStatusesFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(getHealthIntegrationStatusesFailure),
            map(() => showToasterMessage({ message: 'Failed to load health integration statuses' }))
        )
    );
}
